import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as userStore from '../utils/userStore';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // ---------- 弹框状态 ----------
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginModalMode, setLoginModalMode] = useState('login'); // 'login' | 'register'
  const [redirectPath, setRedirectPath] = useState(null);
  const navigate = useNavigate();

  // 初始化检查本地会话
  useEffect(() => {
    const sessionUser = userStore.getSessionUser();
    if (sessionUser) {
      setUser(sessionUser);
    }
    setLoading(false);
  }, []);

  // 打开弹框，可传入目标路径
  const openLoginModal = (path = null) => {
    setRedirectPath(path);
    setLoginModalMode('login');
    setLoginModalOpen(true);
  };

  /** 打开注册视图弹框 */
  const openRegisterModal = () => {
    setRedirectPath(null);
    setLoginModalMode('register');
    setLoginModalOpen(true);
  };

  const closeLoginModal = () => {
    setLoginModalOpen(false);
    setRedirectPath(null);
    setLoginModalMode('login');
  };

  const afterLogin = (userInfo, token = null) => {
    // 后端返回的 token 优先；本地演示（验证码）流程无 token 时生成模拟 token
    localStorage.setItem('token', token || userStore.buildToken());
    userStore.saveSessionUser(userInfo);
    setUser(userInfo);
    closeLoginModal();
    if (redirectPath) {
      navigate(redirectPath, { replace: true });
    }
    return userInfo;
  };

  // ============ 登录 ============
  const login = async (username, password) => {
    const { token, userInfo } = await userStore.loginByPassword(username, password);
    return afterLogin(userInfo, token);
  };

  /** 验证码登录（手机号/邮箱 + 验证码） */
  const loginByCode = async (contact, code) => {
    const userInfo = await userStore.loginByVerifyCode(contact, code);
    return afterLogin(userInfo);
  };

  // ============ 注册 ============
  /** 用户名注册，成功后自动登录 */
  const register = async (username, password) => {
    const { token, userInfo } = await userStore.registerUser(username, password);
    return afterLogin(userInfo, token);
  };

  /** 验证码注册（用户名 + 手机号/邮箱 + 验证码） */
  const registerByCode = async (username, contact, code) => {
    const userInfo = await userStore.registerUserByCode(username, contact, code);
    return afterLogin(userInfo);
  };

  /** 发送验证码（演示模式返回验证码字符串） */
  const sendCode = (contact) => userStore.sendVerifyCode(contact);

  // ============ 用户中心 ============
  /** 修改昵称 / 头像 */
  const updateProfile = async ({ nickname, avatar }) => {
    if (!user) throw new Error('请先登录');
    const userInfo = await userStore.updateProfile(user.id, { nickname, avatar });
    userStore.saveSessionUser(userInfo);
    setUser(userInfo);
    return userInfo;
  };

  /** 修改密码 */
  const changePassword = async (oldPassword, newPassword) => {
    if (!user) throw new Error('请先登录');
    await userStore.changePassword(user.id, oldPassword, newPassword);
  };

  /** 充值 */
  const recharge = async (amount) => {
    if (!user) throw new Error('请先登录');
    const userInfo = await userStore.recharge(user.id, amount);
    userStore.saveSessionUser(userInfo);
    setUser(userInfo);
    return userInfo;
  };

  /** 从本地刷新用户信息 */
  const refreshUser = () => {
    const sessionUser = userStore.getSessionUser();
    if (sessionUser) setUser(sessionUser);
  };

  const logout = () => {
    userStore.clearSession();
    setUser(null);
    navigate('/');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        setUser,
        loading,
        login,
        loginByCode,
        register,
        registerByCode,
        sendCode,
        updateProfile,
        changePassword,
        recharge,
        refreshUser,
        logout,
        loginModalOpen,
        openLoginModal,
        openRegisterModal,
        closeLoginModal,
        loginModalMode,
        redirectPath,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
